import logger from 'js/app/loggerSingleton';
import user from 'js/lib/user';

import { getChatTargetingRule } from 'bundles/common/utils/chatLoaderUtils';
import BlueJaysExperiments from 'bundles/epic/clients/BlueJays';

const getUrlExclusionsRegex = () => {
  const urlExclusionsPattern =
    '^/(?:programs|purchase|teach|admin|business|degrees|degree-description|internal|o/[a-zA-Z0-9-]+/admin|learn/[a-zA-Z0-9-]+/(?!lecture|supplement|videos|course-manager|graded|ungraded|wiseFlow|programming|quiz|exam|team|irt|peer|discussionPrompt|team|assignment-submission|placeholder))';

  return new RegExp(urlExclusionsPattern);
};

export const getAdminChatTargetingRule = (url: string) => {
  const adminChatTargetingRule = [
    {
      urlPattern: '^/o/[a-zA-Z0-9-]+/admin/home$',
    },
  ];

  try {
    return adminChatTargetingRule.find((rule) => new RegExp(rule.urlPattern).test(url));
  } catch (err) {
    logger.error(
      'Error with one or more admin chat targeting rules. Check that all urlPattern regexes are valid.',
      err
    );
    return undefined;
  }
};

export const getEducatorChatTargetingRule = (url: string) => {
  const educatorChatTargetingRule = [
    {
      urlPattern: '^/teach/[a-zA-Z0-9-]+/course/overview$',
    },
    {
      urlPattern: '^/admin$',
    },
    {
      urlPattern: '^/admin/courses$',
    },
    {
      urlPattern: '^/admin/institutions/settings/staff$',
    },
    {
      urlPattern: '^/admin/institutions/settings/university-assets$',
    },
  ];

  try {
    return educatorChatTargetingRule.find((rule) => new RegExp(rule.urlPattern).test(url));
  } catch (err) {
    logger.error(
      'Error with one or more educator chat targeting rules. Check that all urlPattern regexes are valid.',
      err
    );
    return undefined;
  }
};

export const previewUserAdminEducator = () => {
  // `isAdmin` was previously fetched from an Epic using this targeting rule: https://tools.coursera.org/epic/userTargeting/3KpAUGIPEe2nJF8hSyMGEg@1
  // The query tied to the rule currently returns no rows, so this value is always false
  // TODO fetch the Enterprise admin flag based on the user
  const isAdmin = false;
  const isEducator = user.get().is_staff;
  return { isAdmin, isEducator };
};

export const getShowGlobalHelpButton = (urlPath = location.pathname) => {
  /*
  The global help button is used in the following:
  1. On most learner facing pages to link a user to the help center

  The Boost chat button and global help button are in the same location on the page. The Boost chat button overrides the global help button on a pages where a user might see both, they only see the Boost button.
  */

  const isCourserian = user.isCourserian();
  // Using preview() because we want to check if a user is in an experiment without creating an impression. The impression point should be when a user clicks the global help or chat button.
  const isTargetedByBoostEpic = BlueJaysExperiments.preview('enableChat');
  const { isAdmin, isEducator } = previewUserAdminEducator();

  if (!user.get()?.authenticated) {
    return { showGlobalHelpButton: false }; // don't show global help button to anonymous users
  }
  // User is on an educator chat page && is educator && is not a Courserian
  if (!!getEducatorChatTargetingRule(urlPath) && isEducator && !isCourserian) {
    return { showGlobalHelpButton: true, isEducatorPage: true };
  }
  // User is on an admin chat page && is admin && is not a Courserian
  if (!!getAdminChatTargetingRule(urlPath) && isAdmin && !isCourserian) {
    return { showGlobalHelpButton: true, isAdminPage: true };
  }
  // User is in boost chat experiment && not on boost chat page && on learner global help page
  if (isTargetedByBoostEpic && !getChatTargetingRule(urlPath) && !getUrlExclusionsRegex().test(urlPath)) {
    return { showGlobalHelpButton: true };
  }
  // User not in boost chat experiment && on learner global help page
  if (!isTargetedByBoostEpic && !getUrlExclusionsRegex().test(urlPath)) {
    return { showGlobalHelpButton: true };
  }
  return { showGlobalHelpButton: false };
};
